import React from 'react';
import { motion } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';

// Articles focused on random animal generator keyword
const randomAnimalArticles = [
  {
    id: 1,
    title: "What Is a Random Animal Generator & How Does It Work?",
    type: "Informational",
    content: `
      <p>A random animal generator is a fun digital tool that provides users with unexpected animal facts, images, or names at the click of a button. Whether for education, entertainment, or creative inspiration, these generators pull data from extensive databases to display creatures from lions to rare deep-sea species.</p>
      
      <p>Most random animal generators work via APIs (like Dog CEO or Some Random API) or preloaded datasets. Users simply visit the website, hit "Generate," and receive randomized results—perfect for teachers, writers, or trivia lovers. Advanced versions even categorize animals by habitat, diet, or conservation status.</p>
      
      <p>Why use one? They're excellent for:</p>
      <ul>
        <li>Learning biodiversity interactively</li>
        <li>Breaking creative blocks (e.g., writers/artists)</li>
        <li>Gaming (random animal challenges)</li>
      </ul>
      
      <p>Looking for a reliable tool? Search for "random animal generator" to find options with high-quality images and scientific facts.</p>
    `
  },
  {
    id: 2,
    title: "Top 3 Random Animal Generators for Education",
    type: "Listicle",
    content: `
      <p>Teachers and students adore random animal generators for dynamic learning. Here are the top 3 picks:</p>
      
      <h3>1. National Geographic Kids' Creature Feature</h3>
      <ul>
        <li>Curates animals with vivid photos/videos</li>
        <li>Includes conservation stats (ideal for projects)</li>
      </ul>
      
      <h3>2. Some Random API's Animal Generator</h3>
      <ul>
        <li>Free API with facts + images (e.g., "Did you know a pangolin's scales are made of keratin?")</li>
        <li>Simple integration for coding lessons</li>
      </ul>
      
      <h3>3. Switch Zoo</h3>
      <ul>
        <li>Mix-and-match species (e.g., "What if a zebra had eagle wings?")</li>
        <li>Teaches ecosystems and adaptation</li>
      </ul>
      
      <p>Pro Tip: Pair these with a "random habitat generator" for immersive biology lessons!</p>
    `
  },
  {
    id: 3,
    title: "Creative Uses for Random Animal Generators",
    type: "Inspirational",
    content: `
      <p>Beyond education, random animal generators spark creativity:</p>
      
      <ul>
        <li><strong>Writing Prompts:</strong> Generate a random animal as your protagonist (e.g., "A dung beetle's journey").</li>
        <li><strong>Art Challenges:</strong> Draw the first creature that appears!</li>
        <li><strong>Party Games:</strong> "Act out the animal" with friends.</li>
      </ul>
      
      <p>For extra fun, combine with a "random adjective generator" (e.g., "playful octopus" → instant character idea!).</p>
      
      <p>Tools like Random.org's Animal Generator or bespoke websites make exploration effortless.</p>
    `
  },
  {
    id: 4,
    title: "Why Random Animal Generators Boost Website Engagement",
    type: "SEO/Marketing",
    content: `
      <p>Integrating a random animal generator can:</p>
      
      <ul>
        <li>✅ <strong>Increase Dwell Time:</strong> Users stay longer to click "Generate" repeatedly.</li>
        <li>✅ <strong>Encourage Shares:</strong> Quirky results go viral on social media.</li>
        <li>✅ <strong>Support Backlinks:</strong> Educational sites link to your tool as a resource.</li>
      </ul>
      
      <p>Optimization tips:</p>
      <ul>
        <li>Use semantic HTML tags (<code>&lt;article&gt;</code>, <code>&lt;figure&gt;</code>).</li>
        <li>Add schema markup for "Educational Tool."</li>
        <li>Target long-tail keywords like "free random animal generator for kids".</li>
      </ul>
      
      <p>Example meta description:<br/>
      "Instantly discover fascinating creatures with our random animal generator—perfect for teachers, parents, and curious minds!"</p>
    `
  }
];

/**
 * Articles page component
 * Displays a collection of articles about random animal generators
 */
const Articles = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background-light to-background">
      {/* Header */}
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-6">
        {/* Page title */}
        <div className="max-w-4xl mx-auto mb-10 text-center">
          <motion.h1 
            className="text-3xl md:text-4xl font-bold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Random Animal Generator Articles
          </motion.h1>
          <motion.p 
            className="text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Explore our collection of articles about random animal generators, their uses, and benefits
          </motion.p>
        </div>
        
        {/* Articles section */}
        <section className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {randomAnimalArticles.map((article, index) => (
              <motion.article 
                key={article.id}
                className="bg-white rounded-xl shadow-card overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
              >
                <div className="p-6 md:p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">{article.title}</h2>
                    <span className="bg-primary-light text-primary-dark text-xs font-semibold px-2.5 py-1 rounded">
                      {article.type}
                    </span>
                  </div>
                  
                  <div 
                    className="prose max-w-none text-gray-600"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                  
                  <div className="mt-6 flex justify-between items-center">
                    <button className="btn-outline">
                      Read Full Article
                    </button>
                    
                    <div className="flex space-x-2">
                      <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                        </svg>
                      </button>
                      <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
        
        {/* Back to generator button */}
        <div className="text-center mt-10">
          <a href="/" className="btn-primary inline-block">
            Try Our Random Animal Generator
          </a>
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Articles; 